import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronRight, ExternalLink, FolderKanban, ImagePlus, ListChecks, Mail, Printer, Settings, Star, Store, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ProjectPicker } from "@/components/admin/ProjectPicker";
import { AppointmentCard } from "@/components/admin/AppointmentCard";

const LINKS = [
  ["/admin/messages", "Messages", "Customers asking you to contact them", Mail, "messages"],
  ["/admin/appointments", "Appointments", "Calendar of drop-offs and pickups", CalendarDays, "appointments"],
  ["/admin/projects", "Projects", "Post photos & videos of your work", FolderKanban, "projects"],
  ["/admin/reviews", "Reviews", "Approve and add customer reviews", Star, "reviews"],
  ["/admin/business", "Business Info", "Name, phone numbers, location, hours, logo", Store, "business"],
  ["/admin/content", "Why Choose Us & Services", "Bullet points and service list", ListChecks, "content"],
  ["/admin/flyers", "Flyers", "Download printable flyers (color or black & white)", Printer, "flyers"],
  ["/admin/settings", "Settings", "Alert emails and review approval", Settings, "settings"],
  ["/admin/account", "Account", "Change password or login email", UserCog, "account"],
];

const CountTile = ({ to, label, value, testId }) => (
  <Link to={to} className="rounded-lg border border-zinc-800 bg-[#1A1A1E] p-3 transition-colors hover:border-[#FFD23F]/60 sm:p-4" data-testid={`${testId}-link`}>
    <p className="font-display text-4xl text-[#FFD23F]" data-testid={testId}>{value ?? "–"}</p>
    <p className="mt-1 text-sm leading-tight text-zinc-300">{label}</p>
  </Link>
);

const QuickAction = ({ icon: Icon, label, testId, ...props }) => {
  const Comp = props.to ? Link : "button";
  return (
    <Comp {...props} className="flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-lg bg-[#FFD23F] px-3 text-center text-[#121214] transition-transform hover:-translate-y-0.5" data-testid={testId}>
      <Icon className="h-7 w-7" strokeWidth={2.2} />
      <span className="font-sub text-base font-semibold uppercase leading-tight tracking-wide">{label}</span>
    </Comp>
  );
};

export default function Dashboard() {
  const { data } = useFetch(() => api.get("/admin/dashboard"), []);
  const [picker, setPicker] = useState(false);
  return (
    <AdminLayout title="Dashboard" back={null} testId="admin-dashboard">
      <div className="mb-6 grid grid-cols-2 gap-3">
        <QuickAction icon={ImagePlus} label="Add Photos & Videos" onClick={() => setPicker(true)} type="button" testId="quick-add-media-button" />
        <QuickAction icon={Star} label="Add Review" to="/admin/reviews?add=1" testId="quick-add-review-button" />
      </div>
      <ProjectPicker open={picker} onOpenChange={setPicker} />
      <div className="mb-6 grid grid-cols-3 gap-3">
        <CountTile to="/admin/messages" label="New messages" value={data?.new_messages} testId="dashboard-count-new-messages" />
        <CountTile to="/admin/reviews?tab=pending" label="Pending reviews" value={data?.pending_reviews} testId="dashboard-count-pending-reviews" />
        <CountTile to="/admin/appointments" label="Upcoming appointments" value={data?.upcoming_7d} testId="dashboard-count-upcoming" />
      </div>
      <section className="mb-6 rounded-lg border border-zinc-800 bg-[#1A1A1E] p-4" data-testid="dashboard-today">
        <h2 className="font-sub mb-3 text-lg font-semibold uppercase tracking-wide text-[#FAF7F2]">Today's drop-offs</h2>
        {data && data.today_dropoffs.length === 0 && <p className="text-zinc-400" data-testid="dashboard-no-dropoffs">No drop-offs today</p>}
        <div className="space-y-2">{data?.today_dropoffs.map((a) => <AppointmentCard key={a.id} a={a} />)}</div>
      </section>
      <div className="mb-6 grid grid-cols-3 gap-3 text-center text-sm text-zinc-400">
        <p><span className="font-display block text-2xl text-zinc-200" data-testid="dashboard-count-in-progress">{data?.in_progress ?? "–"}</span>In Progress</p>
        <p><span className="font-display block text-2xl text-zinc-200" data-testid="dashboard-count-completed">{data?.completed ?? "–"}</span>Completed</p>
        <p><span className="font-display block text-2xl text-zinc-200" data-testid="dashboard-count-reviews">{data?.visible_reviews ?? "–"}</span>Published reviews</p>
      </div>
      <nav className="space-y-3" data-testid="admin-nav">
        {LINKS.map(([to, label, sub, Icon, id]) => (
          <Link key={to} to={to} className="flex min-h-[72px] items-center gap-4 rounded-lg border border-zinc-800 bg-[#1A1A1E] px-4 py-3 transition-colors hover:border-[#FFD23F]/60" data-testid={`dashboard-link-${id}`}>
            <Icon className="h-6 w-6 shrink-0 text-[#FFD23F]" />
            <span className="min-w-0 flex-1">
              <span className="font-sub block text-lg font-semibold uppercase tracking-wide text-[#FAF7F2]">{label}</span>
              <span className="block text-sm text-zinc-400">{sub}</span>
            </span>
            <ChevronRight className="h-5 w-5 text-zinc-500" />
          </Link>
        ))}
        <a href="/" target="_blank" rel="noreferrer" className="flex min-h-[64px] items-center gap-4 rounded-lg border border-dashed border-zinc-700 px-4 py-3 text-zinc-200 hover:border-[#FFD23F]/60" data-testid="dashboard-link-view-live-site">
          <ExternalLink className="h-6 w-6 text-[#FFD23F]" />
          <span className="font-sub text-lg font-semibold uppercase tracking-wide">View live site</span>
        </a>
      </nav>
    </AdminLayout>
  );
}
